import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, RotateCcw, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

// Helper for conditional classes - Updated Build
const classNames = (...classes) => classes.filter(Boolean).join(' ');

const ExcelDataGrid = ({
    columns = [],
    data = [],
    onDataChange,
    onRowAdd,
    onRowDelete,
    getRowClassName,       // Function(row) -> string
    isRowDeleteDisabled,   // Function(row) -> boolean
    onRowRevert,           // Function(row) -> void
    height = '600px',
    stickyFirstColumn = false,
    stickyColumns: stickyColumnsProp,  // number of columns to freeze from left
    summaryRow = null,
    rowsPerPage = 50 // Default pagesize
}) => {
    // Resolve how many columns to freeze
    const stickyColumns = stickyColumnsProp != null ? stickyColumnsProp : (stickyFirstColumn ? 1 : 0);

    // Helper: compute the left pixel offset for a sticky column (idx is 0-based column index)
    // 48px = row number cell width (w-12)
    const getStickyLeft = (colIdx) => {
        const ROW_NUM_WIDTH = 48;
        let left = ROW_NUM_WIDTH;
        for (let i = 0; i < colIdx; i++) {
            const w = parseInt(columns[i]?.width || '120', 10);
            left += w;
        }
        return left;
    };

    const isLastSticky = (colIdx) => colIdx === stickyColumns - 1;
    const [currentPage, setCurrentPage] = useState(1);
    const [activeCell, setActiveCell] = useState(null);
    const gridRef = useRef(null);

    const handlePageChange = (newPage) => {
        setActiveCell(null);
        setCurrentPage(newPage);
    };

    // Calculate Pagination
    const totalPages = Math.max(1, Math.ceil(data.length / rowsPerPage));
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, data.length);
    const currentData = data.slice(startIndex, endIndex);

    // Safe page check
    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [data.length, totalPages, currentPage]);

    // Handle cell value changes (maps page index to global data index)
    const handleCellChange = (pageRowIndex, columnKey, value) => {
        const globalRowIndex = startIndex + pageRowIndex;
        const newData = [...data];
        newData[globalRowIndex] = { ...newData[globalRowIndex], [columnKey]: value };
        onDataChange(newData, globalRowIndex, columnKey);
    };

    // Auto-switch page when active cell changes (e.g. searching or external focus)
    useEffect(() => {
        if (activeCell) {
            const pageForCell = Math.floor(activeCell.row / rowsPerPage) + 1;
            if (pageForCell !== currentPage) {
                setCurrentPage(pageForCell);
            }
        }
    }, [activeCell, rowsPerPage, currentPage]);

    // Focus effect
    useEffect(() => {
        // Only run if active cell is on THIS page
        if (activeCell && gridRef.current) {
            const pageForCell = Math.floor(activeCell.row / rowsPerPage) + 1;
            if (pageForCell === currentPage) {
                // Determine the row index relative to current page (0 to 49)
                const relativeRowIndex = activeCell.row % rowsPerPage;
                const selector = `[data-row="${relativeRowIndex}"][data-col="${activeCell.col}"]`;

                // Allow a brief tick for render
                setTimeout(() => {
                    const element = gridRef.current?.querySelector(selector);
                    if (element) element.focus();
                }, 0);
            }
        }
    }, [activeCell, currentPage, rowsPerPage]);

    // Handle keyboard navigation
    const handleKeyDown = (e, pageRowIndex, colIndex) => {
        const globalRowIndex = startIndex + pageRowIndex;

        if (e.key === 'Enter') {
            e.preventDefault();
            if (globalRowIndex < data.length - 1) {
                setActiveCell({ row: globalRowIndex + 1, col: columns[colIndex].key });
            } else if (onRowAdd) {
                onRowAdd();
                // We don't set active cell here immediately because the new row might be on next page 
                // data.length will increase, effect will trigger.
                // Just ensuring we try to focus the new row:
                setTimeout(() => setActiveCell({ row: globalRowIndex + 1, col: columns[colIndex].key }), 50);
            }
        } else if (e.key === 'Tab') {
            e.preventDefault();
            if (colIndex < columns.length - 1) {
                setActiveCell({ row: globalRowIndex, col: columns[colIndex + 1].key });
            } else if (globalRowIndex < data.length - 1) {
                setActiveCell({ row: globalRowIndex + 1, col: columns[0].key });
            } else if (onRowAdd) {
                onRowAdd();
                setTimeout(() => setActiveCell({ row: globalRowIndex + 1, col: columns[0].key }), 50);
            }
        } else if (e.key === 'ArrowUp') {
            if (globalRowIndex > 0) {
                setActiveCell({ row: globalRowIndex - 1, col: columns[colIndex].key });
            }
            // If going up crosses page boundary, the `useEffect` on `activeCell` handles page switching
        } else if (e.key === 'ArrowDown') {
            if (globalRowIndex < data.length - 1) {
                setActiveCell({ row: globalRowIndex + 1, col: columns[colIndex].key });
            }
        }
    };

    // Auto-format numbers on blur
    const handleBlur = (pageRowIndex, col, currentValue) => {
        if (col.type === 'number' && currentValue !== '' && currentValue !== null && currentValue !== undefined) {
            const num = parseFloat(currentValue);
            if (!isNaN(num)) {
                const rounded = num.toFixed(2);
                if (currentValue !== rounded) {
                    handleCellChange(pageRowIndex, col.key, rounded);
                }
            }
        }
    };

    return (
        <div className="border border-gray-300 rounded-lg shadow-sm bg-white flex flex-col overflow-hidden h-full">
            {/* Grid Container */}
            <div
                className="overflow-auto relative flex-1"
                ref={gridRef}
            >
                <table className="border-collapse table-fixed min-w-full text-sm">
                    {/* Force exact column widths so sticky offsets always match */}
                    <colgroup>
                        <col style={{ width: '48px', minWidth: '48px' }} />
                        {columns.map((col) => (
                            <col key={col.key} style={{ width: col.width || '120px', minWidth: col.width || '120px' }} />
                        ))}
                        <col style={{ width: '64px', minWidth: '64px' }} />
                    </colgroup>
                    <thead className="sticky top-0 z-20 bg-gray-100 ring-1 ring-gray-200 shadow-sm">
                        <tr>
                            {/* Row Number Header */}
                            <th className="sticky left-0 z-30 w-12 bg-gray-200 border-b border-r border-gray-300 p-0 text-center text-sm font-semibold text-gray-500 shadow-sm">
                                <div className="p-2 flex items-center justify-center">#</div>
                            </th>

                            {/* Data Headers */}
                            {columns.map((col, idx) => {
                                const isSticky = idx < stickyColumns;
                                const isLast = isLastSticky(idx);
                                return (
                                    <th
                                        key={col.key}
                                        className={classNames(
                                            "p-0 bg-gray-100 border-b border-r border-gray-300 font-semibold text-gray-700 text-xs selection:bg-blue-100",
                                            isSticky ? "sticky z-20" : "",
                                            isSticky && isLast ? "shadow-[2px_0_5px_-2px_rgba(0,0,0,0.15)]" : ""
                                        )}
                                        style={{
                                            minWidth: col.width || '120px',
                                            width: col.width,
                                            ...(isSticky ? { left: `${getStickyLeft(idx)}px` } : {})
                                        }}
                                    >
                                        <div className="flex items-center justify-center px-1 py-3 text-center break-words min-h-[48px]">
                                            {col.label}
                                            {col.required && <span className="text-red-500 ml-1">*</span>}
                                        </div>
                                    </th>
                                );
                            })}
                            <th className="w-16 bg-gray-100 border-b border-gray-300 p-2 text-center text-gray-700 font-semibold">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white">
                        {currentData.map((row, index) => {
                            const globalIndex = startIndex + index;
                            return (
                                <tr
                                    key={row.id || globalIndex}
                                    className={classNames(
                                        "group hover:bg-gray-50/50",
                                        getRowClassName ? getRowClassName(row, globalIndex) : ""
                                    )}
                                >
                                    {/* Row Number */}
                                    <td className="sticky left-0 z-10 w-12 bg-inherit border-r border-b border-gray-200 text-center text-sm text-gray-500 font-medium select-none group-hover:bg-gray-100">
                                        {globalIndex + 1}
                                    </td>

                                    {/* Data Cells */}
                                    {columns.map((col, colIndex) => {
                                        const isSticky = colIndex < stickyColumns;
                                        const isLast = isLastSticky(colIndex);
                                        const isDisabled = typeof col.disabled === 'function' ? col.disabled(row) : col.disabled;
                                        const isReadOnly = typeof col.readOnly === 'function' ? col.readOnly(row) : col.readOnly;
                                        const isGray = isDisabled || isReadOnly;

                                        return (
                                            <td
                                                key={`${globalIndex}-${col.key}`}
                                                className={classNames(
                                                    "p-0 border-r border-b border-gray-200 relative",
                                                    isGray ? "bg-gray-100/90" : "bg-inherit",
                                                    isSticky ? "sticky z-10" : "",
                                                    isSticky && isLast ? "shadow-[2px_0_5px_-2px_rgba(0,0,0,0.08)]" : "",
                                                    activeCell?.row === globalIndex && activeCell?.col === col.key ? "ring-2 ring-inset ring-blue-500 z-20" : ""
                                                )}
                                                style={{
                                                    minWidth: col.width || '120px',
                                                    width: col.width,
                                                    ...(isSticky ? { left: `${getStickyLeft(colIndex)}px` } : {})
                                                }}
                                            >
                                                {col.type === 'select' ? (
                                                    <select
                                                        data-row={index} // Needs relative index for selector query
                                                        data-col={col.key}
                                                        className={classNames(
                                                            "w-full h-9 px-2 bg-transparent border-none outline-none text-sm focus:ring-0 appearance-none disabled:bg-transparent disabled:text-gray-500"
                                                        )}
                                                        value={row[col.key] || ''}
                                                        onChange={(e) => handleCellChange(index, col.key, e.target.value)}
                                                        onKeyDown={(e) => handleKeyDown(e, index, colIndex)}
                                                        onFocus={() => setActiveCell({ row: globalIndex, col: col.key })}
                                                        disabled={Boolean(typeof col.disabled === 'function' ? col.disabled(row) : (typeof col.readOnly === 'function' ? col.readOnly(row) : (col.disabled || col.readOnly)))}
                                                    >
                                                        <option value="">{col.required ? '' : '-- Select --'}</option>
                                                        {col.options && col.options.map(opt => (
                                                            <option key={opt.value} value={opt.value}>
                                                                {opt.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <input
                                                        data-row={index} // Needs relative index for selector query
                                                        data-col={col.key}
                                                        type={col.type === 'date' ? 'date' : 'text'}
                                                        inputMode={col.type === 'number' ? 'decimal' : 'text'}
                                                        pattern={col.type === 'number' ? '[0-9]*\\.?[0-9]*' : undefined}
                                                        className={classNames(
                                                            "w-full h-9 px-2 bg-transparent border-none outline-none text-sm focus:ring-0 disabled:bg-transparent disabled:text-gray-500 text-ellipsis",
                                                            col.type === 'number' ? 'text-right' : ''
                                                        )}
                                                        value={(row[col.key] !== undefined && row[col.key] !== null) ? String(row[col.key]) : ''}
                                                        onChange={(e) => {
                                                            // For number fields, only allow numeric input (including negative numbers)
                                                            if (col.type === 'number') {
                                                                const value = e.target.value;
                                                                // Allow empty, negative sign, numbers, and single decimal point
                                                                if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
                                                                    handleCellChange(index, col.key, value);
                                                                }
                                                            } else {
                                                                handleCellChange(index, col.key, e.target.value);
                                                            }
                                                        }}
                                                        onBlur={(e) => handleBlur(index, col, e.target.value)}
                                                        onKeyDown={(e) => handleKeyDown(e, index, colIndex)}
                                                        onFocus={() => setActiveCell({ row: globalIndex, col: col.key })}
                                                        readOnly={typeof col.readOnly === 'function' ? col.readOnly(row) : col.readOnly}
                                                        disabled={typeof col.disabled === 'function' ? col.disabled(row) : col.disabled}
                                                        placeholder={col.placeholder || ''}
                                                    />
                                                )}
                                            </td>
                                        );
                                    })}

                                    {/* Actions */}
                                    <td className="w-16 border-b border-gray-200 text-center">
                                        <div className="flex items-center justify-center space-x-1 h-full w-full">
                                            {onRowRevert && (
                                                <button
                                                    onClick={() => onRowRevert(row)}
                                                    className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                                                    title="Revert changes"
                                                >
                                                    <RotateCcw className="w-4 h-4" />
                                                </button>
                                            )}

                                            {(!isRowDeleteDisabled || !isRowDeleteDisabled(row)) && (
                                                <button
                                                    onClick={() => onRowDelete(row.id || globalIndex)}
                                                    className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    title="Delete row"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}

                        {/* Add Row Button (Shown on LAST page only) */}
                        {onRowAdd && currentPage === totalPages && (
                            <tr>
                                <td colSpan={columns.length + 2} className="p-0 border-t border-gray-200">
                                    <button
                                        onClick={onRowAdd}
                                        className="flex items-center w-full px-4 py-3 text-sm text-gray-600 hover:bg-gray-50 hover:text-blue-600 transition-colors bg-gray-50/30"
                                    >
                                        <Plus className="w-4 h-4 mr-2" />
                                        Add new row (or press Enter on last cell)
                                    </button>
                                </td>
                            </tr>
                        )}
                    </tbody>
                    {summaryRow && (
                        <tfoot className="bg-gray-50 border-t-2 border-gray-300 font-bold sticky bottom-0 z-20 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)]">
                            <tr>
                                <td className="sticky left-0 bg-gray-50 border-r border-gray-300 p-2 text-center w-12 z-20"></td>
                                {columns.map((col, idx) => (
                                    <td
                                        key={col.key}
                                        className={classNames(
                                            "px-1 py-3 border-r border-gray-300 text-sm whitespace-nowrap overflow-hidden text-ellipsis bg-gray-50 text-right",
                                            stickyFirstColumn && idx === 0 ? "sticky left-12 z-20 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] text-left pl-2" : ""
                                        )}
                                        style={{ minWidth: col.width || '120px', width: col.width }}
                                    >
                                        {summaryRow[col.key]}
                                    </td>
                                ))}
                                <td className="w-16 bg-gray-50 border-b border-gray-200"></td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            {/* Pagination Controls */}
            <div className="bg-gray-50 border-t border-gray-300 p-2 grid grid-cols-3 items-center shrink-0">
                <div className="text-sm text-gray-600 justify-self-start">
                    Showing {startIndex + 1} to {Math.min(endIndex, data.length)} of {data.length} rows
                </div>
                <div className="flex items-center space-x-2 justify-self-center">
                    <button
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="First Page"
                    >
                        <ChevronsLeft className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => handlePageChange(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Previous Page"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>

                    <span className="text-sm font-medium mx-2">
                        Page {currentPage} of {totalPages}
                    </span>

                    <button
                        onClick={() => handlePageChange(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Next Page"
                    >
                        <ChevronRight className="w-5 h-5" />
                    </button>
                    <button
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Last Page"
                    >
                        <ChevronsRight className="w-5 h-5" />
                    </button>

                    <div className="ml-4 flex items-center space-x-2 text-sm text-gray-500">
                        <span>Go to:</span>
                        <input
                            type="number"
                            min="1"
                            max={totalPages}
                            value={currentPage}
                            onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (!isNaN(val) && val >= 1 && val <= totalPages) {
                                    handlePageChange(val);
                                }
                            }}
                            className="w-16 p-1 border rounded text-center"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExcelDataGrid;
